/**
 * Pure rules behind `SearchableSelect`'s text box, kept out of the component
 * so the tests run without a DOM.
 *
 * The rule that matters (tester report 2026-10-08): **typed text never
 * silently vanishes**. Until then the box showed `selected?.label ?? ''`
 * whenever the list was closed, so typing "shamim", finding no match and
 * clicking elsewhere blanked the field — it looked as if the app had thrown
 * the name away. Now an unpicked query stays in the box (muted, so it does
 * not read as a choice) until the person picks, clears or edits it.
 */

export interface ShownTextInput {
  open: boolean;
  query: string;
  selectedLabel: string | null;
}

/** What the input shows. Open: what is being typed. Closed: the choice, else what was typed. */
export function comboboxShownText({ open, query, selectedLabel }: ShownTextInput): string {
  if (open) return query;
  if (selectedLabel != null) return selectedLabel;
  return query;
}

/** True when the box holds typed text that is not a choice (shown muted). */
export function isUnpickedText({ open, query, selectedLabel }: ShownTextInput): boolean {
  return !open && selectedLabel == null && query.trim().length > 0;
}

/**
 * Whether the "+ Create “…”" row is offered: only when the caller supports
 * creating, something is typed, the search has answered, and no option is
 * already that exact name (case-insensitive) — a person should pick the one
 * that exists rather than be invited to make a duplicate.
 */
export function shouldOfferCreate({
  canCreate,
  query,
  loading,
  optionLabels,
}: {
  canCreate: boolean;
  query: string;
  loading: boolean;
  optionLabels: readonly string[];
}): boolean {
  if (!canCreate || loading) return false;
  const q = query.trim().toLowerCase();
  if (!q) return false;
  return !optionLabels.some((l) => l.trim().toLowerCase() === q);
}
